import { EFinancingProgram } from '@if/core/types'
import { FundingComputedDto } from '@src/data/types/FundingComputedDto'
import { FundingInfoDto } from '@src/data/types/FundingInfoDto'
import { getApiClient } from '@src/services/api-client'
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  EditPersonalLoanWorksheetDto,
  EditWorksheetPaymentSplitDto,
  FullCreditApplication,
  PersonalLoanWorksheet,
  PersonalWorksheetRevision,
  WorksheetDecision,
} from '../../types'
import { makeCreate, makeGetListWithIdsFromApi, makeGetSingleFromApi, makeUpdate } from '../make-api'

const SCOPE = 'PersonalLoanWorksheet'
const CREDIT_APP_SCOPE = 'credit-applications'
const DETAIL = 'detail'
const keysFactory = {
  detail: (id: string) => [{ scope: SCOPE, entity: DETAIL, id }] as const,
  creditAppDetail: (id: string, financingProgramId = EFinancingProgram.Personal) =>
    [{ scope: CREDIT_APP_SCOPE, entity: DETAIL, id, financingProgramId }] as const,
}
const baseUrl = '/Applications/personal/{creditApplicationId}/Worksheet'

const personalLoanWorksheetApi = {
  getById: makeGetSingleFromApi<PersonalLoanWorksheet>(baseUrl),
  create: makeCreate<EditPersonalLoanWorksheetDto, PersonalLoanWorksheet>(baseUrl),
  update: makeUpdate<EditPersonalLoanWorksheetDto, PersonalLoanWorksheet>(baseUrl),
  computePersonalLoanFunding: makeCreate<FundingInfoDto, FundingComputedDto>('Applications/ComputeFunding'),
  getWorksheetRevisions: makeGetListWithIdsFromApi<PersonalWorksheetRevision>(`${baseUrl}/Revisions`),
  updateDecision: makeUpdate<WorksheetDecision, PersonalLoanWorksheet>(`${baseUrl}/Decision`),
}

const completeFinalizeHighEstimate: MutationFunction<
  FullCreditApplication,
  { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }
> = async ({ dto, creditApplicationId }) => {
  const apiClient = getApiClient()
  const response = await apiClient.put<FullCreditApplication>(
    `/Applications/personal/${creditApplicationId}/Worksheet/FinalizeHighEstimate`,
    dto,
  )
  return response.data
}

export function useCompleteFinalizeHighEstimate(): [
  MutationFunction<FullCreditApplication, { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { isPending, mutateAsync } = useMutation<
    FullCreditApplication,
    Error,
    { dto: EditWorksheetPaymentSplitDto; creditApplicationId: string }
  >({
    mutationFn: completeFinalizeHighEstimate,
    onSuccess: (data) => {
      queryClient.setQueryData(keysFactory.detail(data.credit.id), data)
    },
  })

  return [mutateAsync, isPending]
}

export default personalLoanWorksheetApi
